import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { useCircleStore } from './stores/circle';
import { useSettingsStore } from './stores/settings';
import { useWebSocket } from './hooks/useWebSocket';
import { idb } from './lib/idb';
import { isStandalone } from './lib/pwa';
import { ToastContainer, toast } from './components/ui/Toast';
import { OfflineBanner } from './components/ui/OfflineBanner';
import { AppShell } from './components/layout/AppShell';
import { Welcome } from './components/onboarding/Welcome';
import { SetName } from './components/onboarding/SetName';
import { CreateCircle } from './components/onboarding/CreateCircle';
import { InvitePartner } from './components/onboarding/InvitePartner';
import { JoinFromInvite } from './components/onboarding/JoinFromInvite';
import { Home } from './pages/Home';
import { CardsPage } from './pages/Cards';
import { CirclePage } from './pages/Circle';
import { Settings } from './pages/Settings';
import { Landing } from './pages/Landing';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Support } from './pages/Support';

function AuthedShell() {
  const circle = useCircleStore((s) => s.circle);
  useWebSocket(circle?.id ?? null);
  return <AppShell />;
}

function AppGate() {
  const ready = useAuthStore((s) => s.ready);
  const user = useAuthStore((s) => s.user);
  const init = useAuthStore((s) => s.init);
  const settings = useSettingsStore((s) => s.settings);
  const hydrate = useSettingsStore((s) => s.hydrate);
  const loadCircle = useCircleStore((s) => s.loadCircle);
  const circle = useCircleStore((s) => s.circle);
  const bootstrapped = useCircleStore((s) => s.bootstrapped);
  const markBootstrapped = useCircleStore((s) => s.markBootstrapped);

  useEffect(() => {
    void init();
  }, [init]);

  useEffect(() => {
    if (user) hydrate(user.settings);
  }, [user, hydrate]);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      markBootstrapped();
      return;
    }
    if (bootstrapped) return;
    void (async () => {
      try {
        const cid = await idb.getCircleId();
        if (cid) await loadCircle(cid);
      } catch {
        await idb.setCircleId(null);
      } finally {
        markBootstrapped();
      }
    })();
  }, [ready, user, bootstrapped, loadCircle, markBootstrapped]);

  if (!ready || (user && !bootstrapped)) {
    return (
      <div className="flex min-h-screen items-center justify-center text-text-muted">
        <span>Loading…</span>
      </div>
    );
  }

  void settings; // ensure settings store subscription
  void circle;
  return null;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const ready = useAuthStore((s) => s.ready);
  const user = useAuthStore((s) => s.user);
  if (!ready) return null;
  if (!user) return <Navigate to="/welcome" replace />;
  return <>{children}</>;
}

function RequireCircle({ children }: { children: React.ReactNode }) {
  const circle = useCircleStore((s) => s.circle);
  const bootstrapped = useCircleStore((s) => s.bootstrapped);
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/welcome" replace />;
  if (!bootstrapped) return null;
  if (!circle) return <Navigate to="/onboarding/circle" replace />;
  return <>{children}</>;
}

function OnboardingRedirect() {
  const user = useAuthStore((s) => s.user);
  const circle = useCircleStore((s) => s.circle);
  const bootstrapped = useCircleStore((s) => s.bootstrapped);
  const navigate = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    if (!user || !bootstrapped) return;
    if (!circle && !loc.pathname.startsWith('/onboarding') && !loc.pathname.startsWith('/join/')) {
      navigate('/onboarding/circle', { replace: true });
    }
  }, [user, circle, bootstrapped, loc.pathname, navigate]);
  return null;
}

function PendingInviteRecovery() {
  const navigate = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    if (!isStandalone()) return;
    if (loc.pathname.startsWith('/join/')) return;
    void (async () => {
      const code = await idb.getPendingInvite();
      if (code) {
        await idb.clearPendingInvite();
        navigate(`/join/${code}`, { replace: true });
      }
    })();
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export default function App() {
  return (
    <>
      <AppGate />
      <PendingInviteRecovery />
      <OnboardingRedirect />
      <OfflineBanner />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/onboarding/name" element={<SetName />} />
        <Route path="/onboarding/circle" element={<RequireAuth><CreateCircle /></RequireAuth>} />
        <Route path="/onboarding/invite" element={<RequireAuth><InvitePartner /></RequireAuth>} />
        <Route path="/join/:code" element={<JoinFromInvite />} />
        <Route path="/app" element={<RequireAuth><RequireCircle><AuthedShell /></RequireCircle></RequireAuth>}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<Home />} />
          <Route path="cards" element={<CardsPage />} />
          <Route path="circle" element={<CirclePage />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer />
    </>
  );
}

export { toast };
